/**
 * SupportReportDialog - the shared "Report a problem" form (ROP-teams, Admin,
 * Risk Management and SysOps panels). Bill 2026-09-11, shared 2026-10-07.
 *
 * The FORM asks what a support engineer always has to ask back: what kind of
 * thing it is, what you were doing, what you expected, what happened, how it
 * should work, which customer or cart. The CAPTURE goes with it: the page,
 * who you are, the build, the background logs (console, uncaught errors,
 * network calls - see capture.ts) and, if you keep it, a picture of the
 * screen taken the moment you clicked the lifebuoy (see screenshot.ts).
 *
 * TRANSPORT is the host app's call to the existing POST /v3/events with
 * eventType 'support.report' (no new endpoint). The Function files the
 * capture and the picture in the document library and DMs the configured
 * people; suggestions are routed to management first.
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Send, Copy, Check, AlertTriangle, LifeBuoy, Image as ImageIcon } from 'lucide-react';
import { readSupportCapture } from './capture';
import type { ScreenPicture } from './screenshot';

export type ReportKind =
  | 'broken' | 'looks-wrong' | 'error-message' | 'how-do-i'
  | 'slow' | 'missing-info' | 'suggestion' | 'other';

export const REPORT_KINDS: ReadonlyArray<{ id: ReportKind; label: string; hint: string }> = [
  { id: 'broken',        label: 'Something is broken',            hint: 'A button does nothing, a page will not load, a step cannot be completed.' },
  { id: 'looks-wrong',   label: 'Something looks wrong',          hint: 'Wrong name, wrong number, wrong status, missing rows.' },
  { id: 'error-message', label: 'I got an error message',         hint: 'A red box or a message you did not expect.' },
  { id: 'how-do-i',      label: "I don't know how to do something", hint: 'You are stuck and Help did not cover it.' },
  { id: 'slow',          label: 'It is slow or hanging',          hint: 'Spinners that never finish, long waits.' },
  { id: 'missing-info',  label: 'Information is missing',         hint: 'Something you expected to see is not there.' },
  { id: 'suggestion',    label: 'Suggestion or change request',   hint: 'It works, but it should work differently. Enhancement and change requests go to management for review first.' },
  { id: 'other',         label: 'Other',                          hint: '' },
];

export interface SupportReporter {
  upn?: string | null;
  displayName?: string | null;
  officerCode?: string | null;
  role?: string | null;
  branch?: string | null;
}

export interface SupportEventWrite {
  eventType: 'support.report';
  eventCategory: 'system';
  severity: 'info' | 'warn';
  actor: string;
  context: string;
  payloadJson: string;
}

export interface SupportReportHostProps {
  /** Shown in the DM so the reader knows which app it came from, e.g. "Risk Management panel". */
  appName: string;
  appVersion: string;
  appChannel?: string;
  appBuild?: string;
  reporter: SupportReporter | null;
  /** Current route, e.g. location.pathname + location.search. */
  page: string;
  /** Posts the event (client.retail.events.write). Throw on failure. */
  submit: (event: SupportEventWrite) => Promise<unknown>;
  /** Service status shown and sent with the report, e.g. { 'Core Director': 'online' }. */
  status?: Record<string, string>;
  /** Host-specific capture (ROP-teams: the redacted debug snapshot). Merged into payload.snapshot. */
  extraSnapshot?: () => Record<string, unknown>;
  /** Told when the dialog opens/closes (ROP-teams starts its service-status check only while open). */
  onOpenChange?: (open: boolean) => void;
}

export interface SupportReportDialogProps extends SupportReportHostProps {
  open: boolean;
  onClose: () => void;
  /** undefined = still being taken, null = could not be taken. */
  picture: ScreenPicture | null | undefined;
}

export function SupportReportDialog(props: SupportReportDialogProps): JSX.Element | null {
  const { open, onClose, picture, appName, appVersion, appChannel, appBuild, reporter, page, submit, status, extraSnapshot } = props;

  const [kind, setKind] = useState<ReportKind>('broken');
  const [attempting, setAttempting] = useState('');
  const [expected, setExpected] = useState('');
  const [actual, setActual] = useState('');
  const [shouldWork, setShouldWork] = useState('');
  const [customerRef, setCustomerRef] = useState('');
  const [contactOk, setContactOk] = useState(true);
  const [includePicture, setIncludePicture] = useState(true);
  const [zoom, setZoom] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSent(null); setError(null); setCopied(false); setIncludePicture(true); setZoom(false);
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const pictureReady = picture !== undefined;
  const canSend = attempting.trim().length > 0 && actual.trim().length > 0 && !busy && (pictureReady || !includePicture);

  const captured = useMemo(() => ({
    page,
    at: new Date().toISOString(),
    app: { name: appName, version: appVersion, channel: appChannel ?? null, build: appBuild ?? null },
    officer: {
      upn: reporter?.upn ?? null,
      officerCode: reporter?.officerCode ?? null,
      role: reporter?.role ?? null,
      branch: reporter?.branch ?? null,
      displayName: reporter?.displayName ?? null,
    },
    status: status ?? {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [open, page, appName, appVersion, appChannel, appBuild, reporter, status]);

  function buildPayload(withPicture: boolean): Record<string, unknown> {
    const logs = readSupportCapture();
    let extra: Record<string, unknown> = {};
    try { extra = extraSnapshot ? extraSnapshot() : {}; } catch (e) { extra = { captureFailed: String(e) }; }
    return {
      form: {
        kind,
        kindLabel: REPORT_KINDS.find((k) => k.id === kind)?.label ?? kind,
        attempting: attempting.trim(),
        expected: expected.trim(),
        actual: actual.trim(),
        shouldWork: shouldWork.trim(),
        customerRef: customerRef.trim(),
        contactOk,
      },
      captured,
      snapshot: {
        // Hosts without their own API log (the panels) report the network log as apiCalls.
        apiCalls: logs.network,
        errors: logs.errors,
        ...extra,
        console: logs.console,
        network: logs.network,
        windowErrors: logs.errors,
      },
      ...(withPicture && picture ? { screenshot: picture } : {}),
    };
  }

  async function send(): Promise<void> {
    if (!canSend) return;
    setBusy(true); setError(null);
    try {
      await submit({
        eventType: 'support.report',
        eventCategory: 'system',
        severity: kind === 'suggestion' ? 'info' : 'warn',
        actor: reporter?.upn ?? reporter?.officerCode ?? 'unknown',
        context: page,
        payloadJson: JSON.stringify(buildPayload(includePicture)),
      });
      setSent(kind === 'suggestion'
        ? 'Sent. Your suggestion goes to management for review first.'
        : 'Sent. The support team gets a Teams message with your report and the captured detail.');
      setAttempting(''); setExpected(''); setActual(''); setShouldWork(''); setCustomerRef(''); setKind('broken');
    } catch (e) {
      setError((e as Error)?.message || 'Could not send the report. Use "Copy report" and paste it into a Teams message.');
    } finally {
      setBusy(false);
    }
  }

  async function copy(): Promise<void> {
    try {
      // The picture is left out of the copy - pasted into a chat it would be an unreadable wall of text.
      await navigator.clipboard.writeText(JSON.stringify(buildPayload(false), null, 2));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setError('Clipboard is not available here.');
    }
  }

  if (!open) return null;
  const hint = REPORT_KINDS.find((k) => k.id === kind)?.hint;
  const statusText = Object.entries(captured.status).map(([k, v]) => `${k} ${v}`).join(' · ');

  return (
    <div className="support-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="support-dialog" role="dialog" aria-modal="true" aria-labelledby="support-title">
        <header className="support-dialog__head">
          <div className="support-dialog__title-wrap">
            <LifeBuoy size={18} aria-hidden />
            <h2 id="support-title" className="support-dialog__title">Report a problem or suggest a change</h2>
          </div>
          <button type="button" className="support-dialog__close" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden />
          </button>
        </header>

        {sent ? (
          <div className="support-dialog__body">
            <p className="support-sent"><Check size={16} aria-hidden /> {sent}</p>
            <p className="support-muted">
              Sent with it: the page you were on, who you are, the app build, the recent activity and
              errors this session recorded{includePicture && picture ? ', and the picture of your screen' : ''}.
              Tax IDs and card numbers are masked.
            </p>
            <div className="support-dialog__actions">
              <button type="button" className="support-btn support-btn--primary" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <div className="support-dialog__body">
            <label className="support-field">
              <span className="support-field__label">What are you reporting?</span>
              <select value={kind} onChange={(e) => setKind(e.target.value as ReportKind)}>
                {REPORT_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
              {hint && <span className="support-field__hint">{hint}</span>}
            </label>

            <label className="support-field">
              <span className="support-field__label">What were you trying to do? <em>required</em></span>
              <textarea rows={2} value={attempting} onChange={(e) => setAttempting(e.target.value)}
                placeholder={kind === 'suggestion' ? 'e.g. Review the disclosures for a joint account' : 'e.g. Activate the debit card for the joint account I just opened'} />
            </label>

            <label className="support-field">
              <span className="support-field__label">What did you expect to happen?</span>
              <textarea rows={2} value={expected} onChange={(e) => setExpected(e.target.value)} />
            </label>

            <label className="support-field">
              <span className="support-field__label">{kind === 'suggestion' ? 'What happens today?' : 'What actually happened?'} <em>required</em></span>
              <textarea rows={3} value={actual} onChange={(e) => setActual(e.target.value)} />
            </label>

            <label className="support-field">
              <span className="support-field__label">How should this work? <span className="support-muted">(optional)</span></span>
              <textarea rows={2} value={shouldWork} onChange={(e) => setShouldWork(e.target.value)} />
            </label>

            <label className="support-field">
              <span className="support-field__label">Which customer or cart, if any? <span className="support-muted">(name or CIF only - never an SSN)</span></span>
              <input type="text" value={customerRef} onChange={(e) => setCustomerRef(e.target.value)} maxLength={120} />
            </label>

            <div className="support-shot">
              <label className="support-check">
                <input type="checkbox" checked={includePicture} disabled={picture === null}
                  onChange={(e) => setIncludePicture(e.target.checked)} />
                Include a picture of my screen
              </label>
              {picture === undefined && <p className="support-muted"><ImageIcon size={13} aria-hidden /> Taking the picture…</p>}
              {picture === null && <p className="support-muted">The picture could not be taken on this device. The report still sends without it.</p>}
              {picture && includePicture && (
                <>
                  <button type="button" className={`support-shot__thumb ${zoom ? 'is-zoomed' : ''}`.trim()}
                    onClick={() => setZoom((z) => !z)} title={zoom ? 'Shrink' : 'Enlarge'}>
                    <img src={picture.dataUrl} alt="Picture of your screen when you clicked Report a problem" />
                  </button>
                  <p className="support-field__hint">
                    Taken when you clicked the button. Tax IDs, card and account numbers are masked, but
                    names and other details on screen can show. It is filed in the bank&rsquo;s document
                    library with your report; the Teams message only links to it. Untick it if you
                    would rather not send it.
                  </p>
                </>
              )}
            </div>

            <label className="support-check">
              <input type="checkbox" checked={contactOk} onChange={(e) => setContactOk(e.target.checked)} />
              It is fine to contact me about this
            </label>

            <details className="support-capture">
              <summary>What else gets sent with this</summary>
              <ul>
                <li>App: {appName} v{appVersion}{appChannel ? ` · ${appChannel}` : ''}</li>
                <li>Page: <code>{captured.page}</code></li>
                <li>You: {[reporter?.displayName, reporter?.officerCode, reporter?.role, reporter?.branch ? `branch ${reporter.branch}` : null].filter(Boolean).join(' · ') || '—'}</li>
                {statusText && <li>Status: {statusText}</li>}
                <li>Recent activity: the last screens&rsquo; network calls (address and result only, never their contents), browser console messages and any errors (tax IDs and card numbers masked)</li>
              </ul>
            </details>

            {error && <p className="support-error"><AlertTriangle size={14} aria-hidden /> {error}</p>}

            <div className="support-dialog__actions">
              <button type="button" className="support-btn" onClick={copy} title="Copy the full report as text">
                {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />} {copied ? 'Copied' : 'Copy report'}
              </button>
              <button type="button" className="support-btn support-btn--primary" onClick={send} disabled={!canSend}>
                <Send size={14} aria-hidden /> {busy ? 'Sending…' : !pictureReady && includePicture ? 'Preparing picture…' : 'Send report'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SupportReportDialog;
